import { BrowserRouter, Route, Routes } from "react-router-dom";
import Home from "./Home";
import { NotFound } from "./components/NotFound";
import Join from "./pages/Join";
import { AuthProvider } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { SignIn } from "./components/auth/SignIn";
import { SignUp } from "./components/auth/SignUp";
import { AuthCallback } from "./components/auth/AuthCallback";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { lazy, Suspense } from "react";
import { Navbar } from "./components/Navbar";
import { OfflineBanner } from "./components/OfflineBanner";

// Route-level code splitting: the landing stays in the entry bundle; every
// heavy authenticated/secondary page loads on navigation (Lighthouse:
// unused-javascript was 1.5s of the mobile LCP budget).
const ChatPage = lazy(() => import("./components/ChatPage").then(m => ({ default: m.ChatPage })));
const VersesPage = lazy(() => import("./components/verses/VersesPage").then(m => ({ default: m.VersesPage })));
const BibleReader = lazy(() => import("./components/bible/BibleReader").then(m => ({ default: m.BibleReader })));
const Admin = lazy(() => import("./components/Admin").then(m => ({ default: m.Admin })));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("./components/TermsOfService").then(m => ({ default: m.TermsOfService })));
const SpiritualAssessment = lazy(() => import("./components/SpiritualAssessment").then(m => ({ default: m.SpiritualAssessment })));
const DailyCheckIn = lazy(() => import("./components/DailyCheckIn").then(m => ({ default: m.DailyCheckIn })));
const GrowthDashboard = lazy(() => import("./components/GrowthDashboard").then(m => ({ default: m.GrowthDashboard })));
const JournalHub = lazy(() => import("./components/journal/JournalHub").then(m => ({ default: m.JournalHub })));
const SOAPJournal = lazy(() => import("./components/journal/SOAPJournal").then(m => ({ default: m.SOAPJournal })));
const PrayerJournal = lazy(() => import("./components/journal/PrayerJournal").then(m => ({ default: m.PrayerJournal })));
const GratitudeJournal = lazy(() => import("./components/journal/GratitudeJournal").then(m => ({ default: m.GratitudeJournal })));
const FreeformJournal = lazy(() => import("./components/journal/FreeformJournal").then(m => ({ default: m.FreeformJournal })));
const ReadingPlansPage = lazy(() => import("./components/verse/ReadingPlansPage").then(m => ({ default: m.ReadingPlansPage })));
const AboutPage = lazy(() => import("./pages/PublicPages").then(m => ({ default: m.AboutPage })));
const DevotionalsPage = lazy(() => import("./pages/PublicPages").then(m => ({ default: m.DevotionalsPage })));
const DownloadPage = lazy(() => import("./pages/PublicPages").then(m => ({ default: m.DownloadPage })));
const FAQPage = lazy(() => import("./pages/PublicPages").then(m => ({ default: m.FAQPage })));
const FeaturesPage = lazy(() => import("./pages/PublicPages").then(m => ({ default: m.FeaturesPage })));
const PricingPage = lazy(() => import("./pages/PublicPages").then(m => ({ default: m.PricingPage })));
const SupportPage = lazy(() => import("./pages/PublicPages").then(m => ({ default: m.SupportPage })));
const StoryboardPage = lazy(() => import("./pages/StoryboardPage").then(m => ({ default: m.StoryboardPage })));
const GalleryPage = lazy(() => import("./pages/GalleryPage").then(m => ({ default: m.GalleryPage })));
const EventsPage = lazy(() => import("./pages/EventsPage").then(m => ({ default: m.EventsPage })));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage").then(m => ({ default: m.ResourcesPage })));
const WaitlistPage = lazy(() => import("./pages/WaitlistPage").then(m => ({ default: m.WaitlistPage })));
const PilotPage = lazy(() => import("./pages/PilotPage").then(m => ({ default: m.PilotPage })));
const ContactPage = lazy(() => import("./pages/ContactPage").then(m => ({ default: m.ContactPage })));
const BlogPage = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage").then(m => ({ default: m.BlogPostPage })));
import { API_URL } from "@/lib/config";
import { useAuth } from "./contexts/AuthContext";
import { useDeepLinks } from "./hooks/useDeepLinks";
import { useScreenTracking } from "./hooks/useScreenTracking";

const API_BASE_URL = API_URL;

// Wrapper for VersesPage with API URL and user ID
function VersesPageWrapper() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <VersesPage apiBaseUrl={API_BASE_URL} userId={user?.id} />
    </div>
  );
}

function DeepLinkHandler({ children }: { children: React.ReactNode }) {
  useDeepLinks();
  return <>{children}</>;
}

function ScreenTracker({ children }: { children: React.ReactNode }) {
  useScreenTracking();
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <DeepLinkHandler>
      <ScreenTracker>
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-[#FAFAF7]">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-neutral-900"></div>
          </div>
        }
      >
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/devotionals" element={<DevotionalsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/storyboard" element={<StoryboardPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/waitlist" element={<WaitlistPage />} />
        <Route path="/pilot" element={<PilotPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/bible" element={<BibleReader />} />
        <Route path="/bible/:book/:chapter" element={<BibleReader />} />
        <Route path="/verses" element={<VersesPageWrapper />} />
        <Route path="/collections/shared/:slug" element={<VersesPageWrapper />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth"
          element={
            <ProtectedRoute>
              <GrowthDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth/assessment"
          element={
            <ProtectedRoute>
              <SpiritualAssessment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth/check-in"
          element={
            <ProtectedRoute>
              <DailyCheckIn />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal"
          element={
            <ProtectedRoute>
              <JournalHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal/soap"
          element={
            <ProtectedRoute>
              <SOAPJournal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal/prayer"
          element={
            <ProtectedRoute>
              <PrayerJournal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal/gratitude"
          element={
            <ProtectedRoute>
              <GratitudeJournal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal/freeform"
          element={
            <ProtectedRoute>
              <FreeformJournal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reading-plans"
          element={
            <ProtectedRoute>
              <ReadingPlansPage apiUrl={API_BASE_URL} />
            </ProtectedRoute>
          }
        />
        <Route path="/join" element={<Join />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </ScreenTracker>
      </DeepLinkHandler>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <OfflineBanner />
        <AppRoutes />
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
